/**
 * PerChildApproxStart · iter303ak-fix23k — Accepted · T10 · Action 15
 * ────────────────────────────────────────────────────────────────────
 * Per-child approximate start-time picker. Replaces the single-row
 * ApproxStartEstimator so operators can set different start times per
 * accepted quote (e.g. Maintenance first, Repairs later).
 *
 * Rows can be dragged (via ↑ / ↓ buttons — no drag lib) to set the
 * execution ORDER, which flows into the letter as "Maintenance first,
 * then Repairs".
 *
 * Writes to captured_data:
 *   per_child_approx_starts: {
 *     [child_id]: { preset_key, label, order, quote_type_ref, variant_label, appraisal_no }
 *   }
 *   approx_start_time_summary: "Maintenance (A5185) — 1-2 weeks; Repairs (A5186) — 1-2 months"
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { CalendarClock, ExternalLink, ArrowUp, ArrowDown, AlertCircle, CheckCircle2, Loader2, CalendarDays, Clock, StickyNote } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const CUSTOM_KEY = '__custom__';

export default function PerChildApproxStart({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;

  // Derive accepted children from upstream decisions (Follow-up A20 or T10 A10).
  const acceptedFromDecisions = useMemo(() => {
    const decisions = capturedData?.decisions
      || capturedData?.accepted_variants_pick?.decisions
      || capturedData?.await_quote_response?.decisions
      || capturedData?.appraisal?.child_decisions
      || capturedData?.appraisal_record?.child_decisions
      || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({
        child_id,
        quote_type_ref: d.quote_type_ref || '',
        variant_label: d.variant_label || '',
        appraisal_no: d.appraisal_no || null,
      }));
  }, [capturedData]);

  const [heuristics, setHeuristics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState(() => {
    // Seed from prior save if present.
    const existing = value?.per_child_approx_starts || capturedData?.per_child_approx_starts || {};
    if (Object.keys(existing).length > 0) {
      return Object.entries(existing)
        .map(([child_id, r]) => ({ ...r, child_id }))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return [];
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const seededRef = useRef(rows.length > 0);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data: cfg } = await api.get('/app-settings/approx-start-heuristics');
      setHeuristics(cfg);
      // Load any existing calendar bookings for this parent — we hydrate
      // scheduled_start / work amount / unit / notes / booking_id per row.
      let priorBookings = [];
      if (appraisalId) {
        try {
          const { data: bookings } = await api.get(`/calendar/bookings?parent_appraisal_id=${appraisalId}`);
          priorBookings = Array.isArray(bookings) ? bookings : [];
        } catch { /* silent */ }
      }
      const bookingByChild = {};
      for (const b of priorBookings) {
        if (b.child_appraisal_id) bookingByChild[b.child_appraisal_id] = b;
      }
      // Seed rows from decisions only once (respect existing selections).
      if (!seededRef.current && acceptedFromDecisions.length > 0) {
        const evaluated = await Promise.all(acceptedFromDecisions.map(async (c) => {
          const priorB = bookingByChild[c.child_id] || {};
          let sug = null;
          try {
            const resp = await api.post('/app-settings/approx-start-heuristics/evaluate', {
              accepted_quote_types: c.quote_type_ref ? [c.quote_type_ref] : [],
            });
            sug = resp.data;
          } catch { /* silent */ }
          return {
            ...c,
            preset_key: sug?.preset_key || '',
            label: sug?.label || '',
            suggested_rule: sug?.rule_matched,
            // iter303ak-fix30 · real calendar fields
            scheduled_start: priorB.scheduled_start || '',
            work_amount: priorB.expected_work_hours ? Number(priorB.expected_work_hours) : Number(priorB.expected_work_days || 1),
            work_unit: priorB.expected_work_hours ? 'hours' : 'days',
            notes: priorB.notes || '',
            booking_id: priorB._id || null,
          };
        }));
        setRows(evaluated.map((r, i) => ({ ...r, order: i })));
        seededRef.current = true;
      } else if (seededRef.current && Object.keys(bookingByChild).length > 0) {
        // Merge in booking fields on refresh without wiping user edits.
        setRows(prev => prev.map(r => {
          if (r.booking_id) return r;
          const b = bookingByChild[r.child_id];
          if (!b) return r;
          return {
            ...r,
            scheduled_start: r.scheduled_start || b.scheduled_start || '',
            work_amount: r.work_amount || (b.expected_work_hours ? Number(b.expected_work_hours) : Number(b.expected_work_days || 1)),
            work_unit: r.work_unit || (b.expected_work_hours ? 'hours' : 'days'),
            notes: r.notes || b.notes || '',
            booking_id: b._id,
          };
        }));
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to load heuristics');
    } finally { setLoading(false); }
  }, [acceptedFromDecisions, appraisalId]);

  useEffect(() => { load(); }, [load]);

  const setPreset = (child_id, presetKey) => {
    const p = (heuristics?.presets || []).find((x) => x.key === presetKey);
    setRows(prev => prev.map(r =>
      r.child_id === child_id
        ? { ...r, preset_key: presetKey, label: presetKey === CUSTOM_KEY ? (r.custom_text || '') : (p?.label || '') }
        : r));
  };

  const setCustom = (child_id, text) => {
    setRows(prev => prev.map(r =>
      r.child_id === child_id
        ? { ...r, preset_key: CUSTOM_KEY, custom_text: text, label: text }
        : r));
  };

  // iter303ak-fix30 · patch calendar fields on a row.
  const setRowField = (child_id, patch) => {
    setRows(prev => prev.map(r => r.child_id === child_id ? { ...r, ...patch } : r));
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[idx], next[j]] = [next[j], next[idx]];
    setRows(next.map((r, i) => ({ ...r, order: i })));
  };

  // Push derived state upstream on every change.
  useEffect(() => {
    if (!onChange) return;
    const per_child = {};
    for (const r of rows) {
      per_child[r.child_id] = {
        preset_key: r.preset_key === CUSTOM_KEY ? null : r.preset_key,
        label: r.label || '',
        order: r.order,
        quote_type_ref: r.quote_type_ref || '',
        variant_label: r.variant_label || '',
        appraisal_no: r.appraisal_no || null,
      };
    }
    const summary = rows
      .filter(r => r.label)
      .map(r => {
        const name = [r.quote_type_ref, r.variant_label].filter(Boolean).join(' · ');
        const no = r.appraisal_no ? ` (A${r.appraisal_no})` : '';
        return `${name}${no} — ${r.label}`;
      })
      .join('; ');
    onChange({
      per_child_approx_starts: per_child,
      approx_start_time_summary: summary,
      approx_start_time: summary, // preserve compat with the single-value token
    });
  }, [rows, onChange]);

  const openAdmin = () => {
    window.open('/admin/approx-start-heuristics', '_blank', 'noopener,noreferrer');
  };

  const advance = async () => {
    const missing = rows.filter(r => !r.label);
    if (missing.length > 0) {
      setErr(`Pick a start-time bucket for every accepted quote (${missing.length} still empty).`);
      return;
    }
    // iter303ak-fix30 · also require a real scheduled_start per row.
    const missingDates = rows.filter(r => !r.scheduled_start);
    if (missingDates.length > 0) {
      setErr(`Pick a scheduled start date for every accepted quote (${missingDates.length} still empty).`);
      return;
    }
    setSaving(true); setErr('');
    try {
      if (appraisalId) {
        const per_child = {};
        for (const r of rows) {
          per_child[r.child_id] = {
            preset_key: r.preset_key === CUSTOM_KEY ? null : r.preset_key,
            label: r.label || '',
            order: r.order,
            quote_type_ref: r.quote_type_ref || '',
            appraisal_no: r.appraisal_no || null,
          };
        }
        const summary = rows.map(r => {
          const name = [r.quote_type_ref, r.variant_label].filter(Boolean).join(' · ');
          const no = r.appraisal_no ? ` (A${r.appraisal_no})` : '';
          return `${name}${no} — ${r.label}`;
        }).join('; ');
        await api.patch(`/appraisals/${appraisalId}`, {
          per_child_approx_starts: per_child,
          approx_start_time: summary,
        });

        // iter303ak-fix30 · upsert one prebooked calendar entry per row.
        const updatedRows = [...rows];
        for (let i = 0; i < updatedRows.length; i++) {
          const r = updatedRows[i];
          const amount = Math.max(Number(r.work_amount || 1), 0.5);
          const isHours = r.work_unit === 'hours';
          // Convert hours to days for span (round up); keep raw hours too.
          const workDays = isHours ? Math.max(Math.ceil(amount / 8), 1) : Math.max(Math.round(amount), 1);
          const workHours = isHours ? Math.round(amount) : 0;
          const payload = {
            parent_appraisal_id: appraisalId,
            child_appraisal_id: r.child_id,
            child_appraisal_no: r.appraisal_no || null,
            quote_type_ref: r.quote_type_ref || '',
            variant_label: r.variant_label || '',
            scheduled_start: r.scheduled_start,
            expected_work_days: workDays,
            expected_work_hours: workHours,
            notes: r.notes || '',
            status: 'prebooked',
          };
          try {
            const { data: booking } = await api.post('/calendar/bookings/upsert-for-child', payload);
            updatedRows[i] = { ...r, booking_id: booking?._id || r.booking_id };
          } catch (e) {
            // Fail loudly — Trevor's spec requires the calendar entry gets written.
            throw new Error(`Booking save failed for ${r.quote_type_ref || 'row'}: ${e?.response?.data?.detail || e?.message}`);
          }
        }
        setRows(updatedRows);
      }
      onNext();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="wf-per-child-approx-start">
      <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4">
        <div className="flex items-start gap-3">
          <CalendarClock className="w-5 h-5 text-sky-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-sky-900">Approximate start times (per accepted quote)</div>
            <div className="text-sm text-sky-800 mt-1">
              {params.prompt || 'Set the start bucket + real scheduled date + expected work time per accepted quote. Each row is auto-prebooked into the calendar. Drag to reorder the execution sequence.'}
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="p-6 text-sm text-slate-400 italic inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading heuristics…
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No accepted quotes found — did you land here without confirming decisions in T10 A10? Go back and pick at least one row as accepted.
        </div>
      )}

      {!loading && rows.length > 0 && heuristics && (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={r.child_id} className="p-3 border border-slate-200 rounded-lg bg-white" data-testid={`wf-per-child-start-row-${r.child_id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex flex-col shrink-0">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move up" data-testid={`wf-per-child-start-up-${r.child_id}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                  <div className="text-[10px] font-bold text-slate-500 text-center">#{i + 1}</div>
                  <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move down" data-testid={`wf-per-child-start-down-${r.child_id}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                    {[r.quote_type_ref, r.variant_label].filter(Boolean).join(' · ') || 'Quote'}
                    {r.appraisal_no && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">
                        A{r.appraisal_no}
                      </span>
                    )}
                  </div>
                  {r.suggested_rule && (
                    <div className="text-[10px] text-emerald-700 mt-0.5">Suggested by <i>{r.suggested_rule}</i></div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 items-center">
                  {(heuristics.presets || []).map((p) => {
                    const active = r.preset_key === p.key;
                    const activeColor = {
                      rose:   'bg-rose-600 border-rose-600 text-white',
                      amber:  'bg-amber-500 border-amber-500 text-white',
                      orange: 'bg-orange-500 border-orange-500 text-white',
                      purple: 'bg-purple-600 border-purple-600 text-white',
                      sky:    'bg-sky-600 border-sky-600 text-white',
                    }[p.color] || 'bg-slate-700 border-slate-700 text-white';
                    return (
                      <button
                        key={p.key}
                        onClick={() => setPreset(r.child_id, p.key)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${active ? activeColor : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'}`}
                        data-testid={`wf-per-child-start-preset-${r.child_id}-${p.key}`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPreset(r.child_id, CUSTOM_KEY)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${r.preset_key === CUSTOM_KEY ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'}`}
                    data-testid={`wf-per-child-start-preset-${r.child_id}-custom`}
                  >
                    ✏️ Custom
                  </button>
                </div>
              </div>
              {r.preset_key === CUSTOM_KEY && (
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <input
                    type="text"
                    value={r.custom_text || ''}
                    onChange={(e) => setCustom(r.child_id, e.target.value)}
                    placeholder="Custom start-time string"
                    className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-sky-500"
                    data-testid={`wf-per-child-start-custom-${r.child_id}`}
                  />
                </div>
              )}
              {/* iter303ak-fix30 · Real calendar fields (scheduled_start · work amount + unit · notes). */}
              <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="md:col-span-4">
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">
                    <CalendarDays className="w-3 h-3 inline mr-1 -mt-0.5" /> Scheduled start <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={r.scheduled_start || ''}
                    onChange={(e) => setRowField(r.child_id, { scheduled_start: e.target.value })}
                    data-testid={`wf-per-child-start-date-${r.child_id}`}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-sky-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">
                    <Clock className="w-3 h-3 inline mr-1 -mt-0.5" /> Amount
                  </label>
                  <input
                    type="number" step="1" min="0.5"
                    value={r.work_amount ?? 1}
                    onChange={(e) => setRowField(r.child_id, { work_amount: e.target.value })}
                    data-testid={`wf-per-child-start-amount-${r.child_id}`}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-sky-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Unit</label>
                  <div className="flex rounded border border-slate-300 overflow-hidden">
                    {['hours', 'days'].map(u => (
                      <button
                        key={u}
                        type="button"
                        onClick={() => setRowField(r.child_id, { work_unit: u })}
                        data-testid={`wf-per-child-start-unit-${r.child_id}-${u}`}
                        className={`flex-1 px-2 py-1 text-[11px] font-semibold ${
                          r.work_unit === u ? 'bg-sky-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {u}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-4">
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">
                    <StickyNote className="w-3 h-3 inline mr-1 -mt-0.5" /> Notes
                  </label>
                  <input
                    type="text"
                    value={r.notes || ''}
                    onChange={(e) => setRowField(r.child_id, { notes: e.target.value })}
                    placeholder="access, parking, key-holder…"
                    data-testid={`wf-per-child-start-notes-${r.child_id}`}
                    className="w-full px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-sky-500"
                  />
                </div>
              </div>
              {r.booking_id && (
                <div className="mt-1 text-[10px] text-emerald-700 italic">✓ Prebooked in calendar (id {String(r.booking_id).slice(-6)})</div>
              )}
            </div>
          ))}
          <div className="text-xs text-slate-500 pt-1 flex items-center justify-between">
            <span>
              {(() => {
                const noBucket = rows.filter(r => !r.label).length;
                const noDate = rows.filter(r => !r.scheduled_start).length;
                if (noBucket === 0 && noDate === 0) return `All ${rows.length} rows set (bucket + date)`;
                const bits = [];
                if (noBucket) bits.push(`${noBucket} row(s) missing bucket`);
                if (noDate) bits.push(`${noDate} row(s) missing start date`);
                return bits.join(' · ');
              })()}
            </span>
            <button
              onClick={openAdmin}
              className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 underline decoration-dotted"
              data-testid="wf-per-child-start-open-admin"
            >
              <ExternalLink className="w-3 h-3" /> Edit heuristics in admin
            </button>
          </div>
        </div>
      )}

      {err && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-per-child-start-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && (
            <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-per-child-start-cancel">Cancel</button>
          )}
          <button
            onClick={advance}
            disabled={saving || rows.some(r => !r.label) || rows.some(r => !r.scheduled_start)}
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-per-child-start-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
